import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useHistory } from "react-router-dom";
import { ArrowLeft, Check, Plus, QrCode, Users } from "lucide-react";
import { useStore } from "../../store/useStore";
import { useToast } from "../../hooks/useToast";
import { getRoleBadge } from "../../logic/households";
import { BottomSheet } from "../ui/BottomSheet";
import { SegmentedControl } from "../ui/SegmentedControl";
import { Avatar } from "../ui/Avatar";
import { Badge } from "../ui/Badge";
import { Button, PrimaryButton } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { IconChip, ListRow } from "../ui/ListRow";
import { SkeletonList } from "../ui/Skeleton";
import { cn } from "../../lib/utils";
import { getErrorMessage } from "../../lib/errors";

export type SwitcherView = "list" | "create" | "join";
type SwitcherTab = "households" | "members";

const TABS = [
  { value: "households", label: "Households" },
  { value: "members", label: "Members" },
] as const;

interface HouseholdSwitcherSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
  /** Open straight into a sub-view (e.g. "join" from the scan button). */
  initialView?: SwitcherView;
}

/**
 * Bottom sheet for switching between households, viewing members of the
 * current household, and creating/joining households.
 */
export function HouseholdSwitcherSheet({
  isOpen,
  onDismiss,
  initialView = "list",
}: HouseholdSwitcherSheetProps) {
  const history = useHistory();
  const user = useStore((s) => s.user);
  const households = useStore((s) => s.households);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const switchHousehold = useStore((s) => s.switchHousehold);
  const householdMembers = useStore((s) => s.householdMembers);
  const fetchHouseholdMembers = useStore((s) => s.fetchHouseholdMembers);
  const createHousehold = useStore((s) => s.createHousehold);
  const joinHousehold = useStore((s) => s.joinHousehold);
  const showToast = useToast((s) => s.showToast);

  const [tab, setTab] = useState<SwitcherTab>("households");
  const [view, setView] = useState<SwitcherView>(initialView);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);

  // Reset to the requested view each time the sheet opens
  useEffect(() => {
    if (isOpen) {
      setView(initialView);
      setTab("households");
    }
  }, [isOpen, initialView]);

  // Load members when the Members tab is shown
  useEffect(() => {
    if (!isOpen || tab !== "members" || !currentHouseholdId) return;
    let cancelled = false;
    setMembersLoading(true);
    fetchHouseholdMembers(currentHouseholdId).finally(() => {
      if (!cancelled) setMembersLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, tab, currentHouseholdId, fetchHouseholdMembers]);

  const myMemberships = userHouseholds.filter((uh) => uh.user_uuid === user?.id);
  const currentHousehold = households.find((h) => h.uuid === currentHouseholdId);
  const currentMembership = myMemberships.find(
    (uh) => uh.household_uuid === currentHouseholdId
  );
  const isParent = currentMembership?.role === "parent";

  const handleDismiss = () => {
    setName("");
    setCode("");
    setSubmitting(false);
    onDismiss();
  };

  const goTo = (path: string) => {
    handleDismiss();
    history.push(path);
  };

  const handleSelect = (householdId: string | null) => {
    if (!householdId) return;
    if (householdId !== currentHouseholdId) {
      switchHousehold(householdId);
      const switchedTo = households.find((h) => h.uuid === householdId);
      if (switchedTo) showToast(`Switched to ${switchedTo.name}`, "success");
    }
    handleDismiss();
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const created = await createHousehold(trimmed);
      if (!created) throw new Error("Failed to create household");
      switchHousehold(created.uuid);
      showToast(`${created.name} created!`, "success");
      goTo("/home");
    } catch (error) {
      console.error("Error creating household:", error);
      showToast("Couldn't create household. Please try again.", "danger");
      setSubmitting(false);
    }
  };

  const handleJoin = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const membership = await joinHousehold(trimmed);
      if (membership?.household_uuid) switchHousehold(membership.household_uuid);
      showToast("Joined household!", "success");
      goTo("/home");
    } catch (error) {
      console.error("Error joining household:", error);
      const raw = getErrorMessage(error, "Invalid invite code");
      // Unique-violation from user_households means they're already in it
      const message = /duplicate key/i.test(raw)
        ? "You're already a member of this household"
        : raw;
      showToast(message, "danger");
      setSubmitting(false);
    }
  };

  const renderSubViewHeader = (title: string, subtitle: string) => (
    <div className="flex items-start gap-2 mb-5">
      <IconButton
        label="Back"
        icon={<ArrowLeft className="w-5 h-5" />}
        onClick={() => setView("list")}
        className="-ml-2"
      />
      <div className="pt-1.5">
        <h2 className="text-xl font-extrabold tracking-tight text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <BottomSheet isOpen={isOpen} onDismiss={handleDismiss} label="Switch household">
      {view === "create" && (
        <form onSubmit={handleCreate}>
          {renderSubViewHeader("Create household", "You'll be the owner")}
          <label htmlFor="new-household-name" className="field-label">
            Household name
          </label>
          <input
            id="new-household-name"
            className="input-field mb-5"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="The Smith Family, Apt 4B…"
            autoComplete="off"
            maxLength={60}
          />
          <PrimaryButton type="submit" disabled={!name.trim()} loading={submitting}>
            Create household
          </PrimaryButton>
        </form>
      )}

      {view === "join" && (
        <form onSubmit={handleJoin}>
          {renderSubViewHeader("Join household", "Enter the invite code you were sent")}
          <label htmlFor="join-invite-code" className="field-label">
            Invite code
          </label>
          <input
            id="join-invite-code"
            className="input-field mb-5 font-mono tracking-wider"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. DEMO123"
            autoComplete="off"
            autoCapitalize="characters"
          />
          <PrimaryButton type="submit" disabled={!code.trim()} loading={submitting}>
            Join household
          </PrimaryButton>
        </form>
      )}

      {view === "list" && (
        <>
          <SegmentedControl
            label="Switcher sections"
            idPrefix="household-switcher"
            options={TABS}
            value={tab}
            onChange={setTab}
            className="mb-4"
          />

          {tab === "households" && (
            <div
              role="tabpanel"
              id="household-switcher-panel-households"
              aria-labelledby="household-switcher-tab-households"
            >
              <ul className="space-y-1 mb-6">
                {myMemberships.map((membership) => {
                  const household = households.find(
                    (h) => h.uuid === membership.household_uuid
                  );
                  if (!household) return null;
                  const isCurrent = household.uuid === currentHouseholdId;
                  const createdByMe = household.created_by_uuid === user?.id;
                  const badge = getRoleBadge(membership.role, createdByMe);
                  return (
                    <li key={membership.uuid}>
                      <button
                        type="button"
                        onClick={() => handleSelect(household.uuid)}
                        aria-current={isCurrent ? "true" : undefined}
                        className={cn(
                          "w-full flex items-center gap-3 rounded-2xl px-2 py-2.5 min-h-[60px] text-left transition-colors",
                          "hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          isCurrent && "bg-surface"
                        )}
                      >
                        <Avatar
                          name={household.name}
                          seed={household.uuid}
                          shape="square"
                          size="lg"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-bold text-foreground truncate">
                              {household.name}
                            </span>
                            <Badge tone={badge.tone}>{badge.label}</Badge>
                          </span>
                          <span className="block text-[13px] text-muted-foreground">
                            {createdByMe ? "Created by you" : "Shared with you"}
                          </span>
                        </span>
                        {isCurrent && (
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground shrink-0"
                            aria-label="Current household"
                          >
                            <Check className="w-4 h-4" strokeWidth={3} aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              <h3 className="px-1 mb-2 text-base font-bold text-foreground">
                Household settings
              </h3>
              <div className="-mx-4">
                {isParent && currentHousehold && (
                  <ListRow
                    title={`Edit “${currentHousehold.name}”`}
                    subtitle="Change name and invite people"
                    leading={
                      <Avatar
                        name={currentHousehold.name}
                        seed={currentHousehold.uuid}
                        shape="circle"
                      />
                    }
                    trailing={
                      <span className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-3.5 py-1.5 text-sm font-bold">
                        Edit
                      </span>
                    }
                    onClick={() => goTo("/settings/household")}
                  />
                )}
                <ListRow
                  title="Create household"
                  subtitle="You'll be the owner"
                  leading={
                    <IconChip>
                      <Plus className="w-5 h-5" />
                    </IconChip>
                  }
                  onClick={() => setView("create")}
                />
                <ListRow
                  title="Join household"
                  subtitle="Use an invite code"
                  leading={
                    <IconChip>
                      <QrCode className="w-5 h-5" />
                    </IconChip>
                  }
                  onClick={() => setView("join")}
                />
              </div>
            </div>
          )}

          {tab === "members" && (
            <div
              role="tabpanel"
              id="household-switcher-panel-members"
              aria-labelledby="household-switcher-tab-members"
            >
              {membersLoading && householdMembers.length === 0 ? (
                <SkeletonList rows={3} />
              ) : (
                <ul className="space-y-1 mb-4">
                  {householdMembers.map((member) => {
                    const isSelf = member.user_uuid === user?.id;
                    const badge = getRoleBadge(
                      member.role,
                      member.user_uuid === currentHousehold?.created_by_uuid
                    );
                    return (
                      <li
                        key={member.membership_uuid}
                        className="flex items-center gap-3 px-2 py-2.5 min-h-[56px]"
                      >
                        <Avatar
                          name={member.name}
                          seed={member.user_uuid}
                          photoUrl={member.profile_photo_url}
                        />
                        <span className="flex-1 min-w-0 font-bold text-foreground truncate">
                          {member.name}
                          {isSelf && (
                            <span className="font-medium text-muted-foreground"> (you)</span>
                          )}
                        </span>
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Button
                variant="secondary"
                block
                leadingIcon={<Users className="w-4 h-4" aria-hidden="true" />}
                onClick={() => goTo("/settings/members")}
              >
                {isParent ? "Manage & invite members" : "View all members"}
              </Button>
            </div>
          )}
        </>
      )}
    </BottomSheet>
  );
}

export default HouseholdSwitcherSheet;
